import { useState, useEffect } from "react"

import ErrorMessage from "components/forms/error-message"
import lodash from "lodash"
import deepdash from "deepdash"
import { lang } from "config"
import Label from "components/forms/label"
import Input from "components/forms/input"
import useProductLineRepositories from "repositories/product-line"
import Description from "components/forms/description"
import nProgress from "nprogress"
import toast from "react-hot-toast"
import SelectDescription from "components/forms/select-description"
import { usePage } from "contexts/page-context"

export default function Form({ data, errors, onSubmit }) {
    const _ = deepdash(lodash)

    const { configuration } = usePage()

    const { data: productLineSelection, isLoading: isLoadingProductLineSelection } = useProductLineRepositories({
        paginate: false
    })

    const id = data?.id

    const assetBaseUrl = process.env.REACT_APP_INVENTORY_API_URL || ""
    const resolveImage = (path) => {
        if (!path) return ""
        if (String(path).startsWith("http")) return path
        const clean = String(path).replace(/^\/+/, "")
        return assetBaseUrl ? `${assetBaseUrl}/${clean}` : `/${clean}`
    }

    const [name, setName] = useState(data?.name ?? null)
    const [slug, setSlug] = useState(data?.slug ?? null)
    const [productLine, setProductLine] = useState(data?.product_line?.id ?? null)

    const [productLineObject, setProductLineObject] = useState()

    const [imageFile, setImageFile] = useState(null)
    const [imagePreview, setImagePreview] = useState(data?.image_url ? resolveImage(data.image_url) : "")
    const [removeImage, setRemoveImage] = useState(false)

    const onPickImage = (e) => {
        const file = e?.target?.files?.[0]
        if (!file) return
        setRemoveImage(false)
        setImageFile(file)
        setImagePreview(URL.createObjectURL(file))
    }

    const clearImage = () => {
        setImageFile(null)
        setImagePreview("")
        setRemoveImage(true)
    }

    useEffect(() => {
        if (data) {
            setName(data.name ?? null)
            setProductLine(data.product_line.id ?? null)
            setSlug(data.slug ?? null)
            setImagePreview(data.image_url ? resolveImage(data.image_url) : "")
        } else {
            if (!configuration('hierarchy').find((row) => row.key === 'product-line').value && configuration('hierarchy').find((row) => row.key === 'product-type').default_parent === 'auto' && !isLoadingProductLineSelection) {
                setProductLine(productLineSelection[0]?.id)
            }
        }
    }, [data, isLoadingProductLineSelection])

    useEffect(() => {
        if (productLine && !isLoadingProductLineSelection) {
            setProductLineObject(productLineSelection.find((row) => row.id === productLine))
        }
    }, [productLine, isLoadingProductLineSelection])

    const handleSubmit = (e) => {
        e.preventDefault()

        if (!nProgress.isStarted()) {
            onSubmit({
                id,
                name,
                slug,
                product_line_id: productLine,
                product_line: productLineObject,
                image: imageFile,
                remove_image: removeImage
            })
        } else {
            toast.error("Previous action is loading")
        }
    }
    return (
        <form onSubmit={handleSubmit} className="mt-8 space-y-8">
            <div className="mt-10 sm:mt-0">
                <div className="md:grid md:grid-cols-3 md:gap-6">
                    <div className="md:col-span-1">
                        <div className="px-4 sm:px-0">
                            <h3 className="text-lg font-medium leading-6 text-gray-900">{lang.main_form}</h3>
                            <p className="mt-1 text-sm text-gray-600">{lang.please_fill_the_provided_form_input}</p>
                        </div>
                    </div>
                    <div className="mt-5 md:col-span-2 md:mt-0">
                        <div className="border sm:rounded-xl">
                            <div className="px-4 py-5 sm:p-6">
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <Label htmlFor="line" value="Product Line" />
                                        <SelectDescription onChange={(value) => setProductLine(value)} selection={productLineSelection} isLoading={isLoadingProductLineSelection} value={productLine} keyValue={(value) => value.id} title={(value) => value.name} placeholder="Select Product Line" />
                                        <ErrorMessage error={errors.product_line_id} />
                                    </div>
                                    <div>
                                        <Label htmlFor="name" value={lang.name} />
                                        <Input placeholder="Input name here" onChange={(e) => setName(e.target.value)} value={name} id="name" error={errors.name} />
                                        <Description error={errors.name} />
                                    </div>
                                    <div>
                                        <Label htmlFor="slug" value={'Slug'} />
                                        <Input placeholder="Input slug here" onChange={(e) => setSlug(e.target.value)} value={slug} id="slug" error={errors.slug} />
                                        <Description error={errors.slug} />
                                    </div>
                                    <div className="col-span-2">
                                        <Label htmlFor="image" value="Gambar" />
                                        <div className="flex items-center gap-4 mt-1">
                                            <div className="flex items-center justify-center w-20 h-20 overflow-hidden border bg-neutral-50 rounded-2xl">
                                                {imagePreview ? (
                                                    <img src={imagePreview} alt="product type" className="object-cover w-full h-full" />
                                                ) : (
                                                    <div className="text-[10px] text-neutral-400 px-2 text-center">Tidak ada gambar</div>
                                                )}
                                            </div>
                                            <div className="flex flex-col gap-2">
                                                <label className="inline-flex items-center px-4 py-2 text-xs border cursor-pointer rounded-xl hover:bg-neutral-50">
                                                    <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={onPickImage} />
                                                    {imagePreview ? "Ganti Gambar" : "Upload Gambar"}
                                                </label>
                                                {imagePreview && (
                                                    <button type="button" onClick={clearImage} className="px-3 py-2 text-xs border rounded-xl hover:bg-neutral-50" title="Hapus gambar">
                                                        Hapus
                                                    </button>
                                                )}
                                                <div className="text-[11px] text-neutral-500">Disarankan 1:1 (square), PNG/JPG/WebP, max 4MB.</div>
                                            </div>
                                        </div>
                                        <ErrorMessage error={errors.image} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="mt-8 text-xs text-right">
                <button type="submit" className="items-center px-6 py-3 text-white transition bg-neutral-800 rounded-xl active:hover:scale-90">
                    <span>{`${_.isEmpty(data) ? lang.create : lang.update}`}</span>
                </button>
            </div>
        </form>
    )
}